import Layout from "../../components/Layout";
import Button from "../../components/Button";
import BreadCrumb from "../../components/BreadCrumb";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useValidator from "../../hooks/useValidator";
import { Gender, NigerianLGAs, CountriesAndState } from "../../utils/DropDown";
import { Icon } from "@iconify/react";
import { httprequest } from "../../data/api";
import Modal from "../../components/Modal";
import { Grid } from "@mui/material";
import InputGroup from "../../components/FormComponent/InputGroup";
import SelectGroup from "../../components/FormComponent/SelectGroup";
import { toast } from "react-toastify";
import useStyles from "./styles";
import Select from "react-select";

const CLASSES_ENDPOINT = "/api/v1/class";
const UPLOAD_ENDPOINT = "/api/v1/upload";
const STUDENT_ENDPOINT = "/api/v1/students";

const CreateStudent = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [uploadForm, setUploadForm] = useState({});
  const [classData, setClassData] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");
  const [studentValidator, showStudentValidation] = useValidator();
  const [generatePassword, setGeneratePassword] = useState(false);
  const [copyModal, setCopyModal] = useState(false);
  const [createdGuardianCredentials, setCreatedGuardianCredentials] = useState({
    email: "",
    password: ""
  });

  const [studentForm, setStudentForm] = useState({
    first_name: "",
    last_name: "",
    admission_date: "",
    class_admitted: "",
    email: "",
    contact: "",
    gender: "",
    age: ""
    // address: ""
  });

  const handleStateChange = (selectedOption) => {
    setStudentForm((prev) => ({
      ...prev,
      state: selectedOption?.value,
      local_government_area: ""
    }));
  };

  const handleCopyModal = () => {
    setCopyModal(!copyModal);
  };

  /**
 * Removes the selected file.
 */
  const handleRemoveFile = () => setSelectedFile("");
  /**
 * Handles file input change and updates file state.
 * @param {Event} e - The file input change event.
 */
  const handleFileChange = (e) => {
    // const { name } = e.target;
    if (e.target.files) setSelectedFile(e.target.files[0]);
    setUploadForm({ ...uploadForm, file: e.target.files[0].name.replaceAll(" ", "-") });
  };

  /**
   * Handles creation of a new student.
   * Includes form validation, optional file upload, and success/error handling.
   */
  const handleCreateStudent = async () => {
    if (studentValidator.allValid()) {
      setIsLoading(true);
      if (selectedFile) {
        const formData = new FormData();
        formData.append("file", selectedFile);
        const response = await httprequest.post(
          `${UPLOAD_ENDPOINT}`, formData
        );
        if (response.status === 200 && response.data.data) {
          const upload_id = response.data.data?.upload_id;

          const data = { ...studentForm, upload_id, generate_password: generatePassword };

          httprequest.post(STUDENT_ENDPOINT, data).then((res) => {
            setIsLoading(false);
            toast.success("Student successfully added to the school");
            if (!generatePassword) {
              setTimeout(() => {
                navigate("/users/students");
              }, 2000);
            }
            // --| Reset resource state to original state
            setStudentForm({ ...studentForm, first_name: "", last_name: "", admission_date: "", class_admitted: "",
              email: "", contact: "", gender: "", age: "" });
            if (generatePassword && res.data?.data) {
              const passwordData = res?.data?.data;
              setCreatedGuardianCredentials({
                email: passwordData.email,
                password: passwordData.password
              });
              setCopyModal(true);
            }
          }).catch((err) => {
            setIsLoading(false);
            toast.error(err?.response?.data.message);
          });
        } else {
          setIsLoading(false);
          toast.error("There is a problem uploading the file");
        }
      } else {
        const formData = {
          ...studentForm,
          generate_password: generatePassword
        };
        httprequest.post(STUDENT_ENDPOINT, formData).then((res) => {
          setIsLoading(false);
          toast.success("Student successfully added to the school");
          if (!generatePassword) {
            setTimeout(() => {
              navigate("/users/students");
            }, 2000);
          }
          if (generatePassword && res.data?.data) {
            const passwordData = res?.data?.data;
            setCreatedGuardianCredentials({
              email: passwordData.email,
              password: passwordData.password
            });
            setCopyModal(true);
          }
          // --| Reset resource state to original state
          setStudentForm({ ...studentForm, first_name: "", last_name: "", admission_date: "", class_admitted: "",
            email: "", contact: "", gender: "", age: "" });
        }).catch((err) => {
          setIsLoading(false);
          toast.error(err?.response?.data.message);
        });
      }
    } else {
      showStudentValidation(true);
    }
  };

  /**
   * Fetches all physical classes and updates the state.
   */
  const getAllClasses = () => {
    httprequest.get(CLASSES_ENDPOINT).then((res) => {
      const physicalClasses = res?.data?.data?.filter((data) => data?.class_type === "physical");
      setClassData(physicalClasses);
    }).catch((err) => {
      toast.error(err?.response?.data);
    });
  };

  /**
 * Handles change in student form inputs.
 * @param {Event} e - The change event object from input.
 */
  const handleStudentChange = (e) => {
    const { name, value } = e.target;

    // Update student form data
    setStudentForm((prev) => ({
      ...prev,
      [name]: value
    }));

    // If country changes, set its states
    if (name === "country") {
      const selectedCountry = CountriesAndState.find(
        (c) => c.name === value
      );

      if (selectedCountry && selectedCountry.states.length > 0) {
      // Map states into react-select options
        const options = selectedCountry.states.map((s) => ({
          value: s,
          label: s
        }));
        setStateOptions(options);
      } else {
        setStateOptions([]);
      }
    }
  };

  /**
 * Destructures the student form state for easy access to individual fields.
 * @typedef {Object} StudentForm
 * @property {string} first_name - First name of the student
 * @property {string} last_name - Last name of the student
 * @property {string} admission_date - Admission date
 * @property {string} class_admitted - Class admitted to
 * @property {string} email - Email address
 * @property {string} contact - Contact number
 * @property {string} gender - Gender of the student
 * @property {number} age - Age of the student
 */
  const { first_name, local_government_area, home_address, country, last_name, admission_date, class_admitted, email, contact,
    gender, age } = studentForm;

  const handleLGAChange = (selectedOption) => {
    setStudentForm((prev) => ({
      ...prev,
      local_government_area: selectedOption?.value
    }));
  };

  // const stateOptions = NigerianStates?.map((state) => ({
  //   value: state.text,
  //   label: state.text
  // }));

  const lgaOptions =
    studentForm.state && NigerianLGAs[studentForm.state]
      ? NigerianLGAs[studentForm.state].map((lga) => ({
        value: lga,
        label: lga
      }))
      : [];

  /**
       * Initializes the component by setting the active students,
       * fetching all student records and class data on mount.
       */
  useEffect(() => {
    getAllClasses();
  }, []);

  return (
    <Layout
      children={
        <>
          <div className="breadcrumb-container">
            <div>
              <BreadCrumb
                link_one="/users/students"
                link_one_name="All Students"
                active="Create Student"
                description="Create a new student profile"
              />
            </div>
          </div>
          <div className="page-content text-left-important">
            <Grid container spacing={2} className="input-padding">
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <InputGroup
                  label="First Name"
                  required
                  margin="normal"
                  inputType="text"
                  inputName="first_name"
                  value={first_name}
                  onChange={handleStudentChange}
                />
                {studentValidator.message("First Name", first_name, "required")}
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <InputGroup
                  required
                  label="Last Name"
                  margin="normal"
                  inputType="text"
                  inputName="last_name"
                  value={last_name}
                  onChange={handleStudentChange}
                />
                {studentValidator.message("Last Name", last_name, "required")}
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <InputGroup
                  label="Other Name"
                  margin="normal"
                  inputType="text"
                  inputName="other_names"
                  onChange={handleStudentChange}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <InputGroup
                  label="Student Email"
                  required
                  margin="normal"
                  placeHolder="Enter email"
                  inputType="email"
                  inputName="email"
                  value={email}
                  onChange={handleStudentChange}
                />
                {studentValidator.message("Email", email, "required")}
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <InputGroup
                  label="Student Contact"
                  required
                  margin="normal"
                  inputType="tel"
                  inputName="contact"
                  value={contact}
                  onChange={handleStudentChange}
                />
                {studentValidator.message("Contact", contact, "required")}
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <InputGroup
                  label="Admission Date"
                  required
                  margin="normal"
                  inputType="date"
                  inputName="admission_date"
                  value={admission_date}
                  onChange={handleStudentChange}
                />
                {studentValidator.message("admission date", admission_date, "required")}
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <SelectGroup required label="Class Admitted to" children={
                  <select value={class_admitted} name="class_admitted" onChange={handleStudentChange}>
                    <option value="">---Select Labels ---</option>
                    {classData?.map((data) => {
                      return (
                        <option key={data?.id} value={data?.id}>{data?.class_name}</option>
                      );
                    })}
                  </select>
                }
                />
                {studentValidator.message("class admitted", class_admitted, "required")}
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <SelectGroup required label="Gender" children={
                  <select value={gender} name="gender" onChange={handleStudentChange}>
                    <option value="">---Select  ---</option>
                    {Gender?.map((gend) => {
                      return (
                        <option key={gend?.key} value={gend?.key}>{gend?.text}</option>
                      );
                    })}
                  </select>
                }
                />
                {studentValidator.message("Gender", gender, "required")}
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <InputGroup
                  label="Age"
                  required
                  margin="normal"
                  inputType="number"
                  inputName="age"
                  value={age}
                  onChange={handleStudentChange}
                />
                {studentValidator.message("Age", age, "required")}
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <InputGroup
                  label="Home Address"
                  margin="normal"
                  inputType="text"
                  inputName="home_address"
                  value={home_address}
                  onChange={handleStudentChange}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <SelectGroup required label="Country" children={
                  <select name="country" onChange={handleStudentChange}>
                    <option value="">---Select  ---</option>
                    {CountriesAndState?.map((country, index) => {
                      return (
                        <option key={index} value={country?.name}>{country?.name}</option>
                      );
                    })}
                  </select>
                }
                />
                {studentValidator.message("Country", country, "required")}
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <div className="multiple-select">
                  <p className="input-picker-title">
                        State of Origin
                  </p>
                  <Select
                    onChange={handleStateChange}
                    options={stateOptions}
                    placeholder="Select State"
                    className="react-select-container"
                    name="state"
                  />
                </div>
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                {studentForm?.country === "Nigeria" ? (
                  <div className="multiple-select">
                    <p className="input-picker-title">
            Local Government
                    </p>
                    <Select
                      onChange={handleLGAChange}
                      options={lgaOptions}
                      placeholder="Select LGA"
                      className="react-select-container"
                      name="local_government_area"
                    />
                  </div>
                ) : (
                  <InputGroup
                    label="Local Government"
                    margin="normal"
                    inputType="text"
                    inputName="local_government_area"
                    value={local_government_area}
                    onChange={handleStudentChange}
                  />
                )}
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <InputGroup
                  label="Town"
                  margin="normal"
                  inputType="text"
                  inputName="town"
                  onChange={handleStudentChange}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <InputGroup
                  label="Region"
                  margin="normal"
                  inputType="text"
                  inputName="region"
                  onChange={handleStudentChange}
                />
              </Grid>
              <Grid item lg={3} md={3} sm={3} xs={6}>
                <InputGroup inputType="number" label="Height(cm)" inputName="height"
                  onChange={handleStudentChange}
                />
              </Grid>
              <Grid item lg={3} md={3} sm={3} xs={6}>
                <InputGroup inputType="number" label="Weight" inputName="weight"
                  onChange={handleStudentChange}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <InputGroup
                  label="Previous School"
                  margin="normal"
                  inputType="text"
                  inputName="previous_school"
                  onChange={handleStudentChange}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <InputGroup
                  label="Class at Previous School"
                  margin="normal"
                  inputType="text"
                  inputName="previous_class_passed"
                  onChange={handleStudentChange}
                />
              </Grid>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <label className={`${classes.label}`}><p>Upload supporting documents</p> </label>
                <div className={classes.pickercontainer}>
                  <input type="file" className={classes.picker} onChange={handleFileChange} name="file" accept=".pdf" />

                  <div className={classes.uploadflex}>
                    <div><Icon icon="icon-park-outline:upload-two" className={classes.uploadicon} /></div>
                    {selectedFile?.name ? (
                      <div>
                        <h2 className={classes.uploadtitle}>{selectedFile?.name}</h2>
                      </div>
                    ) :
                      <div>
                        <p className={classes.uploadtitle}>Tap to Upload</p>
                        <p className={classes.uploadsubtitle}>PDF | 10MB max.
                        </p>
                      </div>}
                  </div>
                  <div className={classes.rightpicker}>
                    {selectedFile?.name ? (
                      <div className={classes.uploadbutton} onClick={handleRemoveFile}><Icon icon="zondicons:minus-solid" /> Remove File</div>
                    ) : null}
                  </div>
                </div>
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <div className={classes.check}>
                  <input
                    type="checkbox"
                    checked={generatePassword}
                    onChange={(e) => setGeneratePassword(e.target.checked)}
                  />
                  <label className="checkbox-label" htmlFor="">
                                    Generate password
                  </label>
                </div>

              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <div className={classes.modalFooterBtn}>
                  <Button variant="primaryoutline" buttonSize="full" color="btnfontprimary" buttonName="Cancel" />
                  <Button variant="primary" buttonSize="full" color="btndefault" buttonName="Add Student" onClick={handleCreateStudent} isLoading={isLoading} />
                </div>
              </Grid>
            </Grid>
            <Modal
              title="Student Successfully Created"
              subtitle="Please send to the immediately because you wont be able to see this password again"
              modalContent={
                <div className={classes.copy}>
                  <Grid container spacing={2} className={classes.copy_div}>
                    <Grid item lg={6} md={6} sm={12} xs={12}
                      className={classes.details}
                    >
                      <span className={classes.details_span}>Email Address</span>
                      <p className={classes.details_p}>{createdGuardianCredentials.email || "Not available"}</p>
                    </Grid>
                    <Grid item lg={6} md={6} sm={12} xs={12}
                      className={classes.details}
                    >
                      <span className={classes.details_span}>Password</span>
                      <p className={classes.details_p}>{createdGuardianCredentials.password || "Not available"}</p>
                    </Grid>
                  </Grid>

                  <div className={classes.note}> <Icon icon="fluent:info-16-regular" width="16" height="16" />NB: Copy and send right away, you wont be able to send it after now</div>
                </div>
              }
              modalFooter={
                <>
                  <Button variant="primary" buttonName="Copy" buttonSize="full" color="btndefault" onClick={() => {
                    navigator.clipboard.writeText(
                      `Email: ${createdGuardianCredentials.email}\nPassword: ${createdGuardianCredentials.password}`
                    );
                    toast.success("Copied to clipboard!");
                  }}
                  />
                </>
              }
              isopen={copyModal}
              closeModal={handleCopyModal}
            />
          </div>
          {/* <ToastContainer /> */}
        </>
      }
    />
  );
};

export default CreateStudent;
